import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, Target, Briefcase, Clock, Wrench, HardHat,
  Users, Monitor, Calendar, UserCheck
} from 'lucide-react';

const services = [
  { icon: Target,    title: 'Project Delivery Excellence', desc: 'End-to-end project delivery methodology proven across complex estates.',
    details: ['5-stage gated delivery framework','Stage-by-stage progress tracking','Quality assurance checkpoints','Handover and close-out protocols'] },
  { icon: Briefcase, title: 'PFI & Complex Contracts', desc: 'Navigate the complexities of PFI contract structures with confidence.',
    details: ['Concession-end readiness','Lifecycle fund management','Variation and change control','SPV stakeholder reporting'] },
  { icon: Clock,     title: 'Lifecycle Management', desc: 'Strategic lifecycle planning from 5-year programmes to 25-year asset strategies.',
    details: ['Long-term asset planning','Condition surveys and assessments','Capital investment prioritisation','Renewal and replacement programmes'] },
  { icon: Wrench,    title: 'Capital & Minor Works', desc: 'Structured delivery of capital projects and minor works programmes.',
    details: ['Programme planning and scheduling','Resource allocation','Budget tracking and forecasting','Progress reporting'] },
  { icon: HardHat,   title: 'CDM & H&S Compliance', desc: 'Robust compliance frameworks that protect people and your organisation.',
    details: ['CDM 2015 compliance','Building Safety Act requirements','Risk assessment frameworks','Safety documentation management'] },
  { icon: Users,     title: 'Leadership & Team Development', desc: 'Build high-performing teams through mentoring, coaching and structured development.',
    details: ['PM capability assessments','One-to-one coaching programmes','Team workshops and training','Succession planning'] },
  { icon: Monitor,   title: 'Videra Implementation', desc: 'Replace spreadsheets with structured digital workflows — powered by Videra.',
    details: ['Videra platform configuration','Data migration and setup','User training and adoption','Change management'] },
];

const engagementModels = [
  { icon: Target,     title: 'Advisory',  duration: 'Day rate or retainer', desc: 'Strategic advice on project delivery challenges. Ideal for organisations needing expert guidance on specific issues.' },
  { icon: Calendar,   title: 'Workshop',  duration: '1–3 days on-site',     desc: 'Intensive sessions for process mapping, team assessments and action plans. Fast results, focused outcomes.' },
  { icon: UserCheck,  title: 'Embedded',  duration: 'Weeks or months',      desc: 'Consultant placed within your team for hands-on delivery, coaching and capability building.' },
  { icon: Briefcase,  title: 'Programme', duration: 'Tailored programme',   desc: 'Structured transformation with milestones, KPIs and senior leadership reviews. Full organisational change.' },
];

const V2Consultancy = () => (
  <div data-testid="v2-consultancy">
    <section className="pt-20 pb-16 md:pt-28 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Consultancy</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05] max-w-4xl">
          The expertise behind the platform.
        </h1>
        <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl">
          Keystone Strategic Consultants brings 20+ years and 1,000+ projects of hands-on experience.
          From advisory to embedded support — we help you implement Videra, embed governance, and build
          delivery capability that lasts.
        </p>

        <div className="mt-10 max-w-3xl bg-slate-50 border border-slate-200 rounded-xl p-6" data-testid="v2-consultancy-positioning">
          <p className="text-base md:text-lg text-slate-700 leading-relaxed">
            We map workflows, fix operational inefficiencies, and implement platforms like Videra that give
            teams a <span className="text-slate-900 font-medium">single source of truth</span>, automated
            governance, and audit-ready reporting.
          </p>
        </div>
      </div>
    </section>

    {/* Services grid */}
    <section className="py-20 md:py-28" data-testid="v2-consultancy-services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="bg-white border border-slate-200 rounded-xl p-7 hover:border-slate-900 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] transition-all"
              data-testid={`v2-service-${i}`}
            >
              <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center mb-5">
                <s.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-base font-medium text-slate-900 mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">{s.desc}</p>
              <ul className="space-y-2">
                {s.details.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-xs text-slate-500">
                    <CheckCircle2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                    {d}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Engagement models */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="v2-engagement-models">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">How we work together</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
            Four flexible engagement models.
          </h2>
          <p className="mt-4 text-slate-600">
            From quick-win advisory to long-term embedded support — tailored to your needs and budget.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {engagementModels.map((m, i) => (
            <motion.div
              key={m.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-white border border-slate-200 rounded-xl p-7"
              data-testid={`v2-engagement-${i}`}
            >
              <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center mb-5">
                <m.icon className="w-5 h-5 text-slate-700" />
              </div>
              <h3 className="text-base font-medium text-slate-900 mb-1">{m.title}</h3>
              <p className="text-xs text-blue-600 font-medium mb-3">{m.duration}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{m.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 md:py-32 bg-slate-950 text-white text-center">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">Let&apos;s discuss your needs.</h2>
        <p className="mt-6 text-lg text-slate-400">Book a discovery call to explore which engagement model is right for you.</p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/contact" className="inline-flex items-center justify-center bg-white text-slate-900 px-8 py-4 font-medium tracking-wide hover:bg-slate-100 transition-all rounded-md group">
            Book a discovery call <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/platform" className="inline-flex items-center justify-center bg-transparent border border-white/30 text-white px-8 py-4 font-medium tracking-wide hover:bg-white/10 transition-all rounded-md">
            Explore the platform
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default V2Consultancy;
